import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useEffect, useMemo, useState } from "react";
import { GripVertical, Pencil, Trash2 } from "lucide-react";
import type { Rule } from "../api/types";
import { useReorderRules, useUpdateRule, useDeleteRule } from "../api/queries";

interface Props {
  rules: Rule[];
  onEdit: (rule: Rule) => void;
}

export default function RulesList({ rules, onEdit }: Props) {
  const [localOrder, setLocalOrder] = useState<number[]>(() => rules.map((r) => r.id));
  const reorderMut = useReorderRules();
  const updateMut = useUpdateRule();
  const deleteMut = useDeleteRule();

  useEffect(() => {
    // Re-sync when server rules change.
    setLocalOrder(rules.map((r) => r.id));
  }, [rules]);

  const rulesById = useMemo(() => new Map(rules.map((r) => [r.id, r])), [rules]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  async function onDragEnd(ev: DragEndEvent) {
    const { active, over } = ev;
    if (!over || active.id === over.id) return;
    const oldIdx = localOrder.indexOf(Number(active.id));
    const newIdx = localOrder.indexOf(Number(over.id));
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(localOrder, oldIdx, newIdx);
    setLocalOrder(next);
    await reorderMut.mutateAsync(next);
  }

  async function toggleEnabled(rule: Rule) {
    await updateMut.mutateAsync({ id: rule.id, enabled: !rule.enabled });
  }

  async function remove(rule: Rule) {
    if (!confirm(`Delete rule "${rule.name}"?`)) return;
    await deleteMut.mutateAsync(rule.id);
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={localOrder} strategy={verticalListSortingStrategy}>
        <ul className="space-y-1">
          {localOrder.map((id) => {
            const rule = rulesById.get(id);
            if (!rule) return null;
            return (
              <SortableRow
                key={id}
                rule={rule}
                onEdit={onEdit}
                onToggle={toggleEnabled}
                onDelete={remove}
              />
            );
          })}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({
  rule,
  onEdit,
  onToggle,
  onDelete,
}: {
  rule: Rule;
  onEdit: (r: Rule) => void;
  onToggle: (r: Rule) => void;
  onDelete: (r: Rule) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rule.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-md border border-slate-800 bg-slate-900 px-3 py-2"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab text-slate-500 hover:text-slate-300"
        aria-label="drag handle"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <span
        className="inline-block h-4 w-4 flex-shrink-0 rounded border border-slate-700"
        style={{
          backgroundColor:
            rule.action_kind === "skip"
              ? "transparent"
              : rule.preset?.effect === "off"
                ? "#000"
                : rule.preset?.color || "#666",
          opacity:
            rule.action_kind === "skip"
              ? 0.3
              : rule.preset?.effect === "off"
                ? 0.3
                : (rule.preset?.luminance ?? 100) / 100,
        }}
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`font-medium ${rule.enabled ? "" : "text-slate-500 line-through"}`}>{rule.name}</span>
          <span className="text-xs text-slate-500">#{rule.priority}</span>
          <span className="text-xs text-slate-500">·</span>
          <span className="text-xs text-slate-400">
            {rule.action_kind === "skip" ? "skip" : rule.preset?.name ?? "?"}
          </span>
        </div>
        <div className="truncate text-xs text-slate-500">{predicateSummary(rule)}</div>
      </div>

      <label className="flex items-center gap-1 text-xs text-slate-400">
        <input
          type="checkbox"
          checked={rule.enabled}
          onChange={() => onToggle(rule)}
          className="h-3.5 w-3.5 accent-brand"
        />
        on
      </label>

      <button
        type="button"
        onClick={() => onEdit(rule)}
        className="rounded-md border border-slate-700 p-1 text-slate-400 hover:border-slate-500 hover:text-slate-200"
        aria-label="edit"
      >
        <Pencil className="h-4 w-4" />
      </button>

      <button
        type="button"
        onClick={() => onDelete(rule)}
        className="rounded-md border border-rose-900/60 p-1 text-rose-400 hover:border-rose-700 hover:bg-rose-900/30 hover:text-rose-200"
        aria-label="delete"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </li>
  );
}

function predicateSummary(rule: Rule): string {
  const p = rule.predicate;
  const parts: string[] = [];
  if (p.title_regex) parts.push(`title=/${p.title_regex}/${p.title_regex_flags ?? ""}`);
  if (p.calendar_name_regex)
    parts.push(`calendar=/${p.calendar_name_regex}/${p.calendar_name_regex_flags ?? ""}`);
  if (p.transparency) parts.push(`transparency=${p.transparency}`);
  if (p.all_day !== null && p.all_day !== undefined) parts.push(`all_day=${p.all_day}`);
  if (p.my_response?.length) parts.push(`my_response∈[${p.my_response.join(",")}]`);
  if (p.visibility?.length) parts.push(`visibility∈[${p.visibility.join(",")}]`);
  if (p.is_primary_calendar !== null && p.is_primary_calendar !== undefined)
    parts.push(`primary=${p.is_primary_calendar}`);
  if (p.event_type?.length) parts.push(`event_type∈[${p.event_type.join(",")}]`);
  if (p.status) parts.push(`status=${p.status}`);
  return parts.length ? parts.join(" · ") : "(match any)";
}
